import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import api from "../api";

// ── helpers ───────────────────────────────────────────────────────────────────
const rupeesToPaise = (r) => Math.round(Number(r) * 100);
const paiseToRupees = (p) => (Number(p) / 100).toFixed(0);

const PLAN_META = {
  monthly:   { label: "Monthly",   period: "/ month",    icon: "📅" },
  quarterly: { label: "Quarterly", period: "/ 3 months", icon: "🗓️" },
  annual:    { label: "Annual",    period: "/ year",      icon: "⭐" },
};

const SPECIAL_META = {
  student: { label: "Student", icon: "🎓", hint: "Valid school / institution ID for the current academic year" },
  senior:  { label: "Senior Citizen", icon: "🧓", hint: "Aadhaar or government ID showing age 60 or above" },
};

// Student / Senior Citizen Annual prices, in rupees (placeholder: 30% off ₹499).
const DEFAULT_SPECIAL = { student: "349", senior: "349" };

/** Percent off the Annual price, one decimal (mirrors the server). */
const discountPercent = (rupees, annualRupees) =>
  annualRupees > 0 ? Math.max(0, Math.round(((annualRupees - rupees) / annualRupees) * 1000) / 10) : 0;
const formatPct = (pct) => (Number.isInteger(pct) ? String(pct) : pct.toFixed(1));

const DEFAULT_FORM = {
  monthly:   { amountRupees: "499", savings: "" },
  quarterly: { amountRupees: "499", savings: "Save ₹48 vs monthly" },
  annual:    { amountRupees: "499", savings: "Save ₹389 vs monthly" },
};

// ── component ─────────────────────────────────────────────────────────────────
const SubscriptionPricingPage = ({ module }) => {
  const [form, setForm]       = useState(DEFAULT_FORM);
  const [special, setSpecial] = useState(DEFAULT_SPECIAL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");

  // Load current pricing
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get("/payment/pricing");
        if (!active) return;
        const p = data.pricing || {};
        setForm({
          monthly:   { amountRupees: paiseToRupees(p.monthly?.amountPaise   ?? 49900), savings: p.monthly?.savings   ?? "" },
          quarterly: { amountRupees: paiseToRupees(p.quarterly?.amountPaise ?? 49900), savings: p.quarterly?.savings ?? "Save ₹48 vs monthly" },
          annual:    { amountRupees: paiseToRupees(p.annual?.amountPaise    ?? 49900), savings: p.annual?.savings    ?? "Save ₹389 vs monthly" },
        });
        // The server always answers with a price (older percentage-only
        // settings are converted against the Annual price).
        const annualPaise = p.annual?.amountPaise ?? 49900;
        const fallback = Math.round(annualPaise * 0.7);
        setSpecial({
          student: paiseToRupees(p.special?.student?.amountPaise ?? fallback),
          senior:  paiseToRupees(p.special?.senior?.amountPaise  ?? fallback),
        });
      } catch (err) {
        if (active) setError(err.response?.data?.message || "Could not load pricing");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleChange = useCallback((planId, field, value) => {
    setForm((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], [field]: value },
    }));
    setSuccess("");
    setError("");
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate
    for (const planId of ["monthly", "quarterly", "annual"]) {
      const rupees = Number(form[planId].amountRupees);
      if (!rupees || rupees < 1) {
        setError(`${PLAN_META[planId].label} price must be at least ₹1`);
        return;
      }
    }

    const annualRupees = Number(form.annual.amountRupees);
    for (const key of ["student", "senior"]) {
      const rupees = Number(special[key]);
      if (!rupees || rupees < 1) {
        setError(`${SPECIAL_META[key].label} price must be at least ₹1`);
        return;
      }
      if (rupees > annualRupees) {
        setError(`${SPECIAL_META[key].label} price can't be higher than the Annual price (₹${annualRupees})`);
        return;
      }
    }

    setSaving(true);
    try {
      const body = {
        special: {
          student: { amountPaise: rupeesToPaise(special.student) },
          senior:  { amountPaise: rupeesToPaise(special.senior) },
        },
      };
      for (const planId of ["monthly", "quarterly", "annual"]) {
        body[planId] = {
          amountPaise: rupeesToPaise(form[planId].amountRupees),
          label:       PLAN_META[planId].label,
          period:      PLAN_META[planId].period,
          savings:     form[planId].savings,
        };
      }
      await api.put("/payment/pricing", body);
      setSuccess("Pricing updated successfully. The payment website will reflect the new prices immediately.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout module={module} title="Subscription Pricing">
      <div style={{ maxWidth: 720 }}>

        {/* Info banner */}
        <div className="warning-banner" style={{ marginBottom: 20 }}>
          <strong>Live pricing</strong> — changes here update the payment website
          immediately. Existing subscribers are not affected.
        </div>

        {error   && <p className="error-banner">{error}</p>}
        {success && <p className="success-banner">{success}</p>}

        {loading ? (
          <p className="loading-text">Loading current pricing…</p>
        ) : (
          <form onSubmit={handleSave}>
            <div style={{ display: "grid", gap: 16, marginBottom: 24 }}>
              {["monthly", "quarterly", "annual"].map((planId) => {
                const meta = PLAN_META[planId];
                return (
                  <div key={planId} className="upload-card" style={{ margin: 0 }}>
                    <h3 style={{ margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{meta.icon}</span>
                      {meta.label}
                      <span style={{ fontSize: 12, fontWeight: 400, color: "#6b7280", marginLeft: 4 }}>
                        {meta.period}
                      </span>
                    </h3>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "10px 16px", alignItems: "end" }}>
                      {/* Price */}
                      <div>
                        <label
                          htmlFor={`price-${planId}`}
                          style={{ display: "block", fontWeight: 600, marginBottom: 5, fontSize: 13 }}
                        >
                          Price (₹)
                        </label>
                        <div style={{ position: "relative" }}>
                          <span style={{
                            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                            color: "#6b7280", fontWeight: 700, pointerEvents: "none",
                          }}>₹</span>
                          <input
                            id={`price-${planId}`}
                            type="number"
                            min="1"
                            step="1"
                            value={form[planId].amountRupees}
                            onChange={(e) => handleChange(planId, "amountRupees", e.target.value)}
                            style={{
                              width: "100%", border: "1px solid #d1d5db", borderRadius: 6,
                              padding: "9px 9px 9px 26px", fontSize: 15, fontWeight: 700,
                            }}
                            required
                          />
                        </div>
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#6b7280" }}>
                          = ₹{Number(form[planId].amountRupees) || 0} &nbsp;·&nbsp;
                          {rupeesToPaise(form[planId].amountRupees)} paise
                        </p>
                      </div>

                      {/* Savings label */}
                      <div>
                        <label
                          htmlFor={`savings-${planId}`}
                          style={{ display: "block", fontWeight: 600, marginBottom: 5, fontSize: 13 }}
                        >
                          Savings label <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
                        </label>
                        <input
                          id={`savings-${planId}`}
                          type="text"
                          placeholder='e.g. "Save ₹389 vs monthly"'
                          value={form[planId].savings}
                          onChange={(e) => handleChange(planId, "savings", e.target.value)}
                          style={{
                            width: "100%", border: "1px solid #d1d5db", borderRadius: 6,
                            padding: "9px 10px", fontSize: 14,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Special plans: a price on the Annual plan, like the plans above */}
            <h3 style={{ margin: "0 0 4px" }}>Special plans (Annual only)</h3>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
              Verified Student and Senior Citizen users get the <strong>Annual</strong> plan at the
              price set here. Monthly and Quarterly stay at the standard price. The discount is
              calculated from the Annual price above. Users are verified in{" "}
              <strong>Special Plan Requests</strong>.
            </p>
            <div style={{ display: "grid", gap: 16, marginBottom: 24 }}>
              {["student", "senior"].map((key) => {
                const meta = SPECIAL_META[key];
                const annualRupees = Number(form.annual.amountRupees) || 0;
                const rupees = Number(special[key]) || 0;
                const pct = discountPercent(rupees, annualRupees);
                const tooHigh = rupees > annualRupees;
                return (
                  <div key={key} className="upload-card" style={{ margin: 0 }}>
                    <h3 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{meta.icon}</span>{meta.label}
                      <span style={{ fontSize: 12, fontWeight: 400, color: "#6b7280", marginLeft: 4 }}>
                        Annual · / year
                      </span>
                    </h3>
                    <p style={{ margin: "0 0 12px", fontSize: 12, color: "#6b7280" }}>{meta.hint}</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "10px 16px", alignItems: "end" }}>
                      <div>
                        <label htmlFor={`special-${key}`} style={{ display: "block", fontWeight: 600, marginBottom: 5, fontSize: 13 }}>
                          Annual price (₹)
                        </label>
                        <div style={{ position: "relative" }}>
                          <span style={{
                            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                            color: "#6b7280", fontWeight: 700, pointerEvents: "none",
                          }}>₹</span>
                          <input
                            id={`special-${key}`}
                            type="number"
                            min="1"
                            max={annualRupees || undefined}
                            step="1"
                            value={special[key]}
                            onChange={(e) => { setSpecial((prev) => ({ ...prev, [key]: e.target.value })); setSuccess(""); setError(""); }}
                            style={{
                              width: "100%", border: `1px solid ${tooHigh ? "#dc2626" : "#d1d5db"}`, borderRadius: 6,
                              padding: "9px 9px 9px 26px", fontSize: 15, fontWeight: 700,
                            }}
                            required
                          />
                        </div>
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#6b7280" }}>
                          = ₹{rupees} &nbsp;·&nbsp; {rupeesToPaise(rupees)} paise
                        </p>
                      </div>
                      <div style={{ fontSize: 13, color: tooHigh ? "#b91c1c" : "#166534", fontWeight: 600, paddingBottom: 22 }}>
                        {tooHigh
                          ? `Higher than the Annual price (₹${annualRupees})`
                          : `${formatPct(pct)}% off the Annual price · saves ₹${Math.max(0, annualRupees - rupees)}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Preview */}
            <div className="coverage-card" style={{ marginBottom: 24 }}>
              <h3 style={{ margin: "0 0 12px" }}>Preview</h3>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {["monthly", "quarterly", "annual"].map((planId) => {
                  const meta    = PLAN_META[planId];
                  const rupees  = Number(form[planId].amountRupees) || 0;
                  const savings = form[planId].savings;
                  return (
                    <div key={planId} style={{
                      flex: "1 1 180px", border: "1px solid #e5e7eb", borderRadius: 10,
                      padding: "14px 16px", background: "#fff",
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                        letterSpacing: "0.07em", color: "#6b7280", marginBottom: 6 }}>
                        {meta.label}
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                        <span style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{rupees}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{meta.period}</span>
                      </div>
                      {savings && (
                        <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600, marginTop: 4 }}>
                          {savings}
                        </div>
                      )}
                    </div>
                  );
                })}
                {["student", "senior"].map((key) => {
                  const annualRupees = Number(form.annual.amountRupees) || 0;
                  const rupees = Number(special[key]) || 0;
                  return (
                    <div key={key} style={{
                      flex: "1 1 180px", border: "1px solid #e5e7eb", borderRadius: 10,
                      padding: "14px 16px", background: "#fff",
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                        letterSpacing: "0.07em", color: "#6b7280", marginBottom: 6 }}>
                        Annual · {SPECIAL_META[key].label}
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                        <span style={{ fontSize: 13, color: "#9ca3af", textDecoration: "line-through" }}>₹{annualRupees}</span>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                        <span style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{rupees}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>/ year</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 600, marginTop: 4 }}>
                        {formatPct(discountPercent(rupees, annualRupees))}% off Annual
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              className="primary-action-btn"
              disabled={saving}
              style={{ minWidth: 160 }}
            >
              {saving ? "Saving…" : "Save pricing"}
            </button>
          </form>
        )}
      </div>
    </AdminLayout>
  );
};

export default SubscriptionPricingPage;
